import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Clock, AlertTriangle, CheckCircle, Timer, Settings } from 'lucide-react';
import { Form } from '@/components/ui/form';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { SettingsCard, SwitchField, SettingsField, EditControls, SectionHeader } from '../ui';
import { 
  ExtendedBusinessDetails, 
  TimeTrackingSettings as TimeTrackingSettingsType, 
  createTimeTrackingDefaults
} from '../utils/business-form-defaults';
import { resetTimeTrackingSettingsForm } from '../utils/form-reset-utils';

// Schema for time tracking settings validation
const timeTrackingSchema = z.object({
  late_arrival_threshold_minutes: z.number()
    .min(1, "Late arrival threshold must be at least 1 minute")
    .max(60, "Late arrival threshold cannot exceed 60 minutes")
    .default(5),
  grace_period_minutes: z.number()
    .min(5, "Grace period must be at least 5 minutes")
    .max(120, "Grace period cannot exceed 120 minutes")
    .default(15),
  auto_missed_after_minutes: z.number()
    .min(0, "Auto-missed threshold must be at least 0 minutes (0 = disabled)")
    .max(480, "Auto-missed threshold cannot exceed 480 minutes")
    .default(0),
  early_arrival_threshold_minutes: z.number()
    .min(1, "Early arrival threshold must be at least 1 minute")
    .max(180, "Early arrival threshold cannot exceed 180 minutes")
    .default(30),
  early_departure_threshold_minutes: z.number()
    .min(1, "Early departure threshold must be at least 1 minute")
    .max(120, "Early departure threshold cannot exceed 120 minutes")
    .default(15),
  enable_overtime_tracking: z.boolean().default(true),
  overtime_threshold_minutes: z.number()
    .min(1, "Overtime threshold must be at least 1 minute")
    .max(120, "Overtime threshold cannot exceed 120 minutes")
    .default(15),
  overtime_timeout_hours: z.number()
    .min(1, "Overtime timeout must be at least 1 hour")
    .max(24, "Overtime timeout cannot exceed 24 hours")
    .default(8),
  require_arrival_confirmation: z.boolean().default(true)
}).refine((data) => data.grace_period_minutes >= data.late_arrival_threshold_minutes, {
  message: "Grace period must be greater than or equal to late arrival threshold",
  path: ["grace_period_minutes"]
});

interface TimeTrackingSettingsProps {
  businessDetails: ExtendedBusinessDetails;
  onUpdateSuccess: (updatedDetails: Partial<ExtendedBusinessDetails>) => void;
}

export const TimeTrackingSettings: React.FC<TimeTrackingSettingsProps> = ({
  businessDetails,
  onUpdateSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [overtimeEnabled, setOvertimeEnabled] = useState(
    businessDetails.enable_overtime_tracking ?? true
  );

  const form = useForm<TimeTrackingSettingsType>({
    resolver: zodResolver(timeTrackingSchema),
    defaultValues: createTimeTrackingDefaults(businessDetails),
  });

  // Effect to reset form when businessDetails prop changes
  useEffect(() => {
    form.reset(createTimeTrackingDefaults(businessDetails));
    setOvertimeEnabled(businessDetails.enable_overtime_tracking ?? true);
  }, [businessDetails, form]);

  // Watch form values and update local state
  useEffect(() => {
    const subscription = form.watch((values) => {
      if (values.enable_overtime_tracking !== undefined) {
        setOvertimeEnabled(values.enable_overtime_tracking);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = async (values: TimeTrackingSettingsType) => {
    setLoading(true);
    try {
      const updates = {
        late_arrival_threshold_minutes: values.late_arrival_threshold_minutes,
        grace_period_minutes: values.grace_period_minutes,
        auto_missed_after_minutes: values.auto_missed_after_minutes,
        early_arrival_threshold_minutes: values.early_arrival_threshold_minutes,
        early_departure_threshold_minutes: values.early_departure_threshold_minutes,
        enable_overtime_tracking: values.enable_overtime_tracking,
        overtime_threshold_minutes: values.overtime_threshold_minutes,
        overtime_timeout_hours: values.overtime_timeout_hours,
        require_arrival_confirmation: values.require_arrival_confirmation,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("businesses")
        .update(updates)
        .eq("id", businessDetails.id);

      if (error) throw error;

      toast.success("Time tracking settings updated successfully!");
      setIsEditing(false);
      onUpdateSuccess(updates);
    } catch (error: unknown) {
      console.error("Error updating time tracking settings:", error);
      toast.error(`Failed to update time tracking settings: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    resetTimeTrackingSettingsForm(form, businessDetails);
    setOvertimeEnabled(businessDetails.enable_overtime_tracking ?? true);
  };

  return (
    <SettingsCard 
      icon={Clock} 
      title="Time Tracking Rules" 
      description="Configure business rules for attendance tracking, lateness detection, and overtime management"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Arrival & Lateness Section */}
          <div className="space-y-4">
            <SectionHeader icon={AlertTriangle} title="Arrival & Lateness" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SettingsField
                type="input"
                name="late_arrival_threshold_minutes"
                label="Late Arrival Threshold"
                placeholder="5"
                inputType="number"
                min="1"
                max="60"
                disabled={!isEditing}
                form={form}
                description="Minutes after scheduled start time before arrival is considered late (1-60 minutes)"
                unit="minutes"
              />
              
              <SettingsField
                type="input"
                name="grace_period_minutes"
                label="Grace Period"
                placeholder="15"
                inputType="number"
                min="5"
                max="120"
                disabled={!isEditing}
                form={form}
                description="Additional grace period before attendance penalties apply (5-120 minutes)"
                unit="minutes"
              />
            </div>
          </div>

          {/* Attendance Management Section */}
          <div className="space-y-4">
            <SectionHeader icon={CheckCircle} title="Attendance Management" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SettingsField
                type="input"
                name="auto_missed_after_minutes"
                label="Auto-Missed Threshold"
                placeholder="0"
                inputType="number"
                min="0"
                max="480"
                disabled={!isEditing}
                form={form}
                description="Minutes after shift start before automatically marking as missed (0 = disabled, 0-480 minutes)"
                unit="minutes"
              />

              <SwitchField
                name="require_arrival_confirmation"
                label="Require Arrival Confirmation"
                description="Employees must confirm arrival via QR scan or manual entry"
                disabled={!isEditing}
                form={form}
                className="flex-col items-start space-y-2"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SettingsField
                type="input"
                name="early_arrival_threshold_minutes"
                label="Early Arrival Threshold"
                placeholder="30"
                inputType="number"
                min="1"
                max="180"
                disabled={!isEditing}
                form={form}
                description="Minutes before shift start when arrival is considered too early (1-180 minutes)"
                unit="minutes"
              />

              <SettingsField
                type="input"
                name="early_departure_threshold_minutes"
                label="Early Departure Threshold"
                placeholder="15"
                inputType="number"
                min="1"
                max="120"
                disabled={!isEditing}
                form={form}
                description="Minutes before shift end when departure is considered early (1-120 minutes)"
                unit="minutes"
              />
            </div>
          </div>

          {/* Overtime Tracking Section */}
          <div className="space-y-4">
            <SectionHeader icon={Timer} title="Overtime Tracking" />
            
            <SwitchField
              name="enable_overtime_tracking"
              label="Enable Overtime Tracking"
              description="Track and flag when employees work beyond scheduled hours"
              disabled={!isEditing}
              form={form}
              className="flex-col items-start space-y-2"
            />

            {/* Overtime threshold and timeout - shown when overtime tracking is enabled */}
            {overtimeEnabled && (
              <div className="ml-6 pl-4 border-l-2 border-muted/50 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SettingsField
                    type="input"
                    name="overtime_threshold_minutes"
                    label="Overtime Threshold"
                    placeholder="15"
                    inputType="number"
                    min="1"
                    max="120"
                    disabled={!isEditing}
                    form={form}
                    description="Minutes beyond scheduled end time before flagging as overtime (1-120 minutes)"
                    unit="minutes"
                  />

                  <SettingsField
                    type="input"
                    name="overtime_timeout_hours"
                    label="Overtime Timeout"
                    placeholder="8"
                    inputType="number"
                    min="1"
                    max="24"
                    disabled={!isEditing}
                    form={form}
                    description="Hours after shift end before overtime status transitions to no departure (1-24 hours)"
                    unit="hours"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Help Information */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <h4 className="text-sm font-medium text-foreground">Configuration Guidelines</h4>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• <strong>Late Threshold:</strong> When arrivals are flagged as late</p>
              <p>• <strong>Grace Period:</strong> Additional buffer before strict penalties</p>
              <p>• <strong>Auto-Missed:</strong> When shifts are automatically marked as no-show (0 = disabled)</p>
              <p>• <strong>Early Arrival:</strong> When arrivals are considered too early</p>
              <p>• <strong>Early Departure:</strong> When departures are flagged as leaving early</p>
              <p>• <strong>Overtime:</strong> Track extended work beyond scheduled hours</p>
              <p>• <strong>Overtime Timeout:</strong> Hours before overtime status changes to "No Departure"</p>
            </div>
          </div>

          <EditControls
            isEditing={isEditing}
            loading={loading}
            onStartEdit={handleStartEditing}
            onCancel={handleCancelEditing}
            startEditText="Configure Time Tracking Rules"
            saveText="Update Time Tracking Settings"
            className="mt-6 border-t border-border pt-4"
          />
        </form>
      </Form>
    </SettingsCard>
  );
};